import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, exhaustMap, firstValueFrom, pipe, switchMap, tap, timeout } from 'rxjs';

import { LoadingService } from '@core/loading/loading.service';

import { LANDING_ROUTE, LOGIN_ROUTE, UNAUTHORIZED_ROUTE } from './auth.paths';
import { AuthApi } from './data-access/auth.api';
import { AUTH_ERROR_MESSAGE_KEYS, AuthErrorKey, toAuthErrorKey } from './models/auth-error';
import { User } from './models/user';
import { SessionToken } from './session-token';
import { PermissionsStore } from '@core/permissions/permissions.store';
import { ProjectContextStore } from '@core/project-context/project-context.store';
import { SESSION_RESTORE_TIMEOUT } from '@shared/global/globals';

export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'error';

export interface Credentials {
  email: string;
  password: string;
}

interface AuthState {
  user: User | null;
  status: AuthStatus;
  errorKey: AuthErrorKey | null;
}

/** Where an expired session was, carried across the teardown and the re-login. */
interface LastSessionInfo {
  /** The in-app URL to return to. */
  url: string;
  /** So a different account signing in on this tab is not dropped onto it. */
  userId: number;
  expiresAt: number;
}

const initialState: AuthState = { user: null, status: 'idle', errorKey: null };

/** How long the user waits behind the loader before we sign out locally anyway. */
const REVOKE_TIMEOUT_MS = 5000;

/**
 * `sessionStorage` rather than the store, because the entry has to outlive the
 * very teardown that empties the store — and it wants exactly the scope the
 * token already has: this tab, until it closes.
 *
 * `endSession()` clears the token by key for the same reason: a blanket
 * `sessionStorage.clear()` there would take this entry with it and the feature
 * would silently stop working.
 */
const LAST_SESSION_INFO = 'last_session_info';

/**
 * Reads the entry and consumes it in the same breath. It is good for one
 * sign-in whether or not it is used, so a later ordinary sign-out and sign-in
 * cannot resurrect a stale destination — and a corrupt entry cannot wedge every
 * future login attempt in the tab.
 */
function takeLastSessionInfo(): LastSessionInfo | null {
  const stored = sessionStorage.getItem(LAST_SESSION_INFO);
  sessionStorage.removeItem(LAST_SESSION_INFO);
  if (stored === null) return null;

  try {
    const parsed = JSON.parse(stored) as Partial<LastSessionInfo>;
    const isComplete =
      typeof parsed?.url === 'string' &&
      typeof parsed.userId === 'number' &&
      typeof parsed.expiresAt === 'number';

    return isComplete ? (parsed as LastSessionInfo) : null;
  } catch {
    return null;
  }
}

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ status, errorKey }) => ({
    isAuthenticating: computed(() => status() === 'authenticating'),
    isAuthenticated: computed(() => status() === 'authenticated'),
    errorMessageKey: computed(() => {
      const key = errorKey();
      return key === null ? null : AUTH_ERROR_MESSAGE_KEYS[key];
    }),
  })),
  withMethods(
    (
      store,
      api = inject(AuthApi),
      token = inject(SessionToken),
      router = inject(Router),
      loading = inject(LoadingService),
      permissionsStore = inject(PermissionsStore),
      projectContextStore = inject(ProjectContextStore),
    ) => {
      /**
       * Set for the whole of an expiry exit. Several in-flight requests can 401
       * at once, and each one would otherwise fire its own teardown — the later
       * ones recording `/unauthorized` itself as the page to come back to,
       * which drops the next sign-in on the 404 instead.
       */
      let leavingOnExpiry = false;

      function endSession(): void {
        token.clear();
        patchState(store, initialState);
      }

      /**
       * The single exit both endings share: drops the local session, leaves for
       * `route`, and only then empties the stores the shell behind it is still
       * rendering from — clearing them before the navigation settles would
       * flash an empty shell on the way out. The loader is released either way.
       *
       * Clearing the token has to come first: `publicGuard` bounces anyone
       * still holding a valid one back to the landing route.
       */
      async function endSessionAndLeave(route: string, replaceUrl = false): Promise<void> {
        endSession();
        return router.navigate([route], { replaceUrl })
          .then(() => {
            permissionsStore.clearPermissions();
            projectContextStore.clearStore();
          })
          .finally(() => loading.hide());
      }

      /**
       * The expiry exit: a 401 on any non-auth endpoint, or a stored token
       * already past its expiry at bootstrap. Records where to come back to,
       * then hands over to the shared teardown.
       */
      async function kickOutFromSession(): Promise<void> {
        if (leavingOnExpiry) return;
        leavingOnExpiry = true;

        const user = store.user();
        // Only the in-app path can name a page worth returning to. At bootstrap
        // the router has not navigated yet (`router.url` is still `/`) and
        // `getMe()` has not run, so there is no account to match against
        // either: that path is meant to land on the landing route, and a
        // half-filled entry would only pretend otherwise.
        if (user) {
          const lastSession: LastSessionInfo = {
            url: router.url,
            userId: user.id,
            expiresAt: Date.now() + SESSION_RESTORE_TIMEOUT,
          };
          sessionStorage.setItem(LAST_SESSION_INFO, JSON.stringify(lastSession));
        }

        return endSessionAndLeave(UNAUTHORIZED_ROUTE, true);
      }

      async function navigateToLanding(): Promise<boolean> {
        return router.navigate([LANDING_ROUTE], { replaceUrl: true });
      }

      /**
       * Returns the user to where their session ended, when the recorded page
       * is still warm and belongs to them; otherwise the landing route.
       *
       * The restored URL is an ordinary navigation, so its guards re-run — a
       * user whose grants changed while they were away is redirected to the 403
       * by `permissionsGuard`, which is the correct outcome. A redirect like
       * that resolves this navigation with `true`: the promise follows the
       * redirect rather than reporting the original URL as unreached. So a
       * `false` here means the navigation genuinely did not happen — rejected
       * outright, or superseded — and the landing route is the right answer.
       * A URL matching nothing is *not* one of those cases: it resolves `true`
       * onto the 404 page, which is a designed exit.
       */
      function resumeAfterLogin(user: User): void {
        const lastSession = takeLastSessionInfo();
        const isResumable =
          lastSession !== null &&
          lastSession.userId === user.id &&
          lastSession.expiresAt > Date.now();

        if (!isResumable) {
          void navigateToLanding();
          return;
        }

        void router
          .navigateByUrl(lastSession.url, { replaceUrl: true })
          .then((restored) => {
            if (!restored) void navigateToLanding();
          })
          .catch(() => void navigateToLanding());
      }

      return {
        /**
         * Sign-in is two chained calls: `/connect/token`, then `/api/User/me`
         * with the fresh token (the header interceptor picks it up on its own).
         * `exhaustMap` ignores submits made while one is already in flight.
         */
        login: rxMethod<Credentials>(
          pipe(
            tap(() => patchState(store, { status: 'authenticating', errorKey: null })),
            exhaustMap((credentials) =>
              api.requestToken(credentials.email, credentials.password).pipe(
                tap((response) => token.set(response.access_token, response.expires_in)),
                switchMap(() => api.getMe()),
                tap((user) => {
                  patchState(store, { user, status: 'authenticated', errorKey: null });
                  // A session is running again, so the next expiry gets its own exit.
                  leavingOnExpiry = false;
                  resumeAfterLogin(user);
                }),
                catchError((error: unknown) => {
                  token.clear();
                  patchState(store, {
                    user: null,
                    status: 'error',
                    errorKey: toAuthErrorKey(error),
                  });
                  return EMPTY; // swallow so the method stays alive for a retry
                }),
              ),
            ),
          ),
        ),

        /**
         * The global loader covers the shell until the revoke settles, so the
         * user is never dropped on the login screen with the call still open.
         * The request goes out before the token is cleared, so it still carries
         * the Bearer header.
         */
        logout(): void {
          loading.show('components.loader.loggingOut');
          // A deliberate sign-out leaves nothing behind to come back to. This
          // cannot live in `endSessionAndLeave()`: `kickOutFromSession()` calls
          // that too, right after writing the entry it depends on.
          sessionStorage.removeItem(LAST_SESSION_INFO);

          api
            .revokeToken()
            .pipe(
              timeout(REVOKE_TIMEOUT_MS),
              // A failed or slow revoke must not strand the user inside the app:
              // completing here funnels both outcomes into the same exit.
              catchError(() => EMPTY),
            )
            .subscribe({
              complete: () => void endSessionAndLeave(LOGIN_ROUTE)
            });
        },

        kickOutFromSession,

        /**
         * Mirrors the project the backend has just recorded as this user's
         * last selection. The user object is replaced rather than mutated, so
         * everything computed from `user()` re-runs — an in-place write would
         * leave the signal untouched and the change unnoticed.
         *
         * A no-op when nobody is signed in.
         */
        setLastProjectSelected(projectId: number): void {
          const user = store.user();
          if (!user) return;

          patchState(store, { user: { ...user, lastProjectSelected: projectId } });
        },

        /**
         * Bootstrap hook: rehydrates the user when the stored token is still
         * valid, and discards the session when the backend rejects it.
         */
        async restoreSession(): Promise<void> {
          if (token.isTokenExpired()) {
            return kickOutFromSession();
          }
          if (!token.hasValidToken()) {
            // Deliberately does not navigate. This runs inside
            // `provideAppInitializer`, and the router skips its initial
            // navigation entirely once any navigation has been requested
            // (`Router.initialNavigation()`), so leaving for `/login` here
            // would rewrite the URL an anonymous visitor actually asked for —
            // taking password-reset links and the white-label login with it.
            // `authGuard` and `publicGuard` already route this case correctly.
            endSession();
            permissionsStore.clearPermissions();
            projectContextStore.clearStore();
            return;
          }

          patchState(store, { status: 'authenticating' });

          return firstValueFrom(
            api.getMe().pipe(
              tap((user) => patchState(store, { user, status: 'authenticated' })),
              catchError(() => {
                endSession();
                return EMPTY;
              }),
            ),
            { defaultValue: undefined },
          ).then(() => undefined);
        },
      };
    },
  ),
);
